import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Unsubscribe — Michael's Painting" },
      { name: "description", content: "Manage your email preferences with Michael's Painting." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type State =
  | { kind: "loading" }
  | { kind: "invalid"; reason: string }
  | { kind: "already" }
  | { kind: "confirm" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

function UnsubscribePage() {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [token, setToken] = useState<string>("");

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token") || "";
    setToken(t);
    if (!t) {
      setState({ kind: "invalid", reason: "Missing token" });
      return;
    }
    (async () => {
      try {
        const res = await fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setState({ kind: "invalid", reason: body?.error || "Invalid or expired link" });
          return;
        }
        if (body?.valid === false && body?.reason === "already_unsubscribed") {
          setState({ kind: "already" });
          return;
        }
        if (body?.valid) {
          setState({ kind: "confirm" });
          return;
        }
        setState({ kind: "invalid", reason: "Invalid response" });
      } catch (e: any) {
        setState({ kind: "error", message: e?.message || "Network error" });
      }
    })();
  }, []);

  async function confirm() {
    setState({ kind: "submitting" });
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setState({ kind: "error", message: body?.error || "Unsubscribe failed" });
        return;
      }
      if (body?.success) setState({ kind: "success" });
      else if (body?.reason === "already_unsubscribed") setState({ kind: "already" });
      else setState({ kind: "error", message: "Unexpected response" });
    } catch (e: any) {
      setState({ kind: "error", message: e?.message || "Network error" });
    }
  }

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 text-center">
        <p className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground">
          Michael's Painting
        </p>
        <h1 className="mt-2 text-2xl font-serif">Email preferences</h1>

        {state.kind === "loading" && (
          <p className="mt-6 text-sm text-muted-foreground">Checking your link…</p>
        )}

        {state.kind === "confirm" && (
          <>
            <p className="mt-4 text-sm text-muted-foreground">
              Unsubscribe from Michael's Painting emails? You can always reach us at (608) 886-4598.
            </p>
            <button
              onClick={confirm}
              className="mt-6 inline-flex items-center rounded-full bg-amber-300 px-6 py-3 text-sm font-semibold text-zinc-950 hover:bg-amber-400 transition-colors"
            >
              Confirm unsubscribe
            </button>
          </>
        )}

        {state.kind === "submitting" && (
          <p className="mt-6 text-sm text-muted-foreground">Updating your preferences…</p>
        )}

        {state.kind === "success" && (
          <p className="mt-6 text-sm">
            You've been unsubscribed. We're sorry to see you go — you can still reach us any time.
          </p>
        )}

        {state.kind === "already" && (
          <p className="mt-6 text-sm text-muted-foreground">
            This email address is already unsubscribed.
          </p>
        )}

        {state.kind === "invalid" && (
          <p className="mt-6 text-sm text-muted-foreground">
            {state.reason}. If you clicked a link from a recent email, please try again or
            contact us directly.
          </p>
        )}

        {state.kind === "error" && (
          <p className="mt-6 text-sm text-red-500">{state.message}</p>
        )}
      </div>
    </main>
  );
}